import { createTelemetryReader, TelemetrySample, TelemetryReader } from './readStream';
import { supabase } from '@/integrations/supabase/client';

export interface TelemetryServiceOptions {
  /** Minimum frames to buffer before ready */
  minFrames?: number;
  /** Maximum frames to keep in memory */
  maxFrames?: number;
}

export interface VideoTelemetryData {
  /** Whether telemetry data is available */
  available: boolean;
  /** Telemetry reader instance */
  reader: TelemetryReader | null;
  /** Error message if loading failed */
  error: string | null;
  /** Loading state */
  loading: boolean;
}

/**
 * Service for loading and managing video telemetry data
 */
export class TelemetryService {
  private readers = new Map<string, TelemetryReader>();
  private loadingPromises = new Map<string, Promise<TelemetryReader | null>>();

  /**
   * Get telemetry data for a video
   */
  async getTelemetryForVideo(
    videoId: string,
    options: TelemetryServiceOptions = {}
  ): Promise<VideoTelemetryData> {
    const { minFrames = 30, maxFrames = 10000 } = options;

    try {
      // Check if we already have a reader for this video
      const existingReader = this.readers.get(videoId);
      if (existingReader) {
        return {
          available: true,
          reader: existingReader,
          error: null,
          loading: false,
        };
      }

      // Check if we're already loading this video
      const existingPromise = this.loadingPromises.get(videoId);
      if (existingPromise) {
        const reader = await existingPromise;
        return {
          available: reader !== null,
          reader,
          error: reader ? null : 'Failed to load telemetry data',
          loading: false,
        };
      }

      // Start loading telemetry data
      const loadPromise = this.loadTelemetryData(videoId, minFrames);
      this.loadingPromises.set(videoId, loadPromise);

      const reader = await loadPromise;
      this.loadingPromises.delete(videoId);

      if (reader) {
        this.readers.set(videoId, reader);
      }

      return {
        available: reader !== null,
        reader,
        error: reader ? null : 'No telemetry data available for this video',
        loading: false,
      };
    } catch (error) {
      console.error('[TelemetryService] Error loading telemetry:', error);
      this.loadingPromises.delete(videoId);
      
      return {
        available: false,
        reader: null,
        error: error instanceof Error ? error.message : 'Unknown error',
        loading: false,
      };
    }
  }

  /**
   * Load telemetry data from storage
   */
  private async loadTelemetryData(
    videoId: string,
    minFrames: number
  ): Promise<TelemetryReader | null> {
    try {
      // First, check if telemetry data exists for this video
      const { data: analysisData, error: analysisError } = await supabase
        .from('swing_analysis_runs')
        .select('id, video_asset_id, ai_mediapipe_v1, ai_meta, storage_path, status')
        .eq('video_asset_id', videoId)
        .eq('status', 'completed')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (analysisError) {
        console.error('[TelemetryService] Error fetching analysis:', analysisError);
        throw new Error(analysisError.message || 'Database error');
      }

      if (!analysisData) {
        console.log('[TelemetryService] No completed analysis found for video:', videoId);
        return null;
      }

      // S-2225: Use ai_mediapipe_v1 or ai_meta for telemetry data (no telemetry_url)
      // Try ai_mediapipe_v1 first (preferred), then ai_meta as fallback
      const telemetryData = analysisData.ai_mediapipe_v1 || analysisData.ai_meta;
      
      if (telemetryData) {
        console.log('[TelemetryService] Extracting telemetry from analysis data');
        return this.createReaderFromAnalysisData(telemetryData);
      }

      console.log('[TelemetryService] No telemetry data available for video:', videoId);
      return null;
    } catch (error) {
      console.error('[TelemetryService] Error loading telemetry data:', error);
      return null;
    }
  }

  /**
   * Create a telemetry reader from analysis data (ai_mediapipe_v1 or ai_meta)
   * S-2225: Resilient parsing with graceful error handling
   */
  private createReaderFromAnalysisData(analysisData: any): TelemetryReader | null {
    try {
      // Validate input data
      if (!analysisData || typeof analysisData !== 'object') {
        console.log('[TelemetryService] Invalid analysis data format');
        return null;
      }

      // Extract pose frames from analysis data
      const frames: TelemetrySample[] = [];
      
      // Handle different data formats gracefully
      let poseFrames = null;
      if (analysisData.pose_frames && Array.isArray(analysisData.pose_frames)) {
        poseFrames = analysisData.pose_frames;
      } else if (analysisData.frames && Array.isArray(analysisData.frames)) {
        poseFrames = analysisData.frames;
      } else if (analysisData.data && Array.isArray(analysisData.data)) {
        poseFrames = analysisData.data;
      }

      if (!poseFrames || !Array.isArray(poseFrames)) {
        console.log('[TelemetryService] No valid frame data found in analysis');
        return null;
      }

      // Process frames with error handling
      poseFrames.forEach((frame: any, index: number) => {
        try {
          // Validate frame structure
          if (!frame || typeof frame !== 'object') {
            return; // Skip invalid frames
          }

          // Extract timestamp with fallback options
          let timestamp = 0;
          if (typeof frame.timestamp === 'number' && frame.timestamp > 0) {
            timestamp = frame.timestamp;
          } else if (typeof frame.t === 'number' && frame.t > 0) {
            timestamp = frame.t * 1000; // Convert seconds to milliseconds
          } else if (typeof frame.time === 'number' && frame.time > 0) {
            timestamp = frame.time;
          } else {
            // Fallback: use index-based timing (assume 30fps)
            timestamp = (index / 30) * 1000;
          }

          // Extract landmarks with fallback
          let landmarks = null;
          if (frame.landmarks && Array.isArray(frame.landmarks)) {
            landmarks = frame.landmarks;
          } else if (frame.keypoints && Array.isArray(frame.keypoints)) {
            landmarks = frame.keypoints;
          } else if (frame.pose && frame.pose.landmarks && Array.isArray(frame.pose.landmarks)) {
            landmarks = frame.pose.landmarks;
          }

          if (landmarks && landmarks.length > 0) {
            frames.push({
              t: timestamp / 1000, // Convert ms to seconds
              width: typeof frame.width === 'number' ? frame.width : 640,
              height: typeof frame.height === 'number' ? frame.height : 480,
              pose: {
                landmarks: [landmarks]
              }
            });
          }
        } catch (frameError) {
          console.warn('[TelemetryService] Skipping malformed frame:', frameError);
          // Continue processing other frames
        }
      });

      if (frames.length === 0) {
        console.log('[TelemetryService] No valid frames found in analysis data');
        return null;
      }

      // Sort frames by timestamp to ensure proper ordering
      frames.sort((a, b) => a.t - b.t);

      console.log(`[TelemetryService] Successfully extracted ${frames.length} frames from analysis data`);

      // Create a simple reader that returns frames by time
      const getFrameAt = (timeSec: number): TelemetrySample | null => {
        if (frames.length === 0) return null;
        
        // Find closest frame <= timeSec
        let bestFrame: TelemetrySample | null = null;
        let bestDiff = Infinity;
        
        for (const frame of frames) {
          const diff = Math.abs(frame.t - timeSec);
          if (frame.t <= timeSec && diff < bestDiff) {
            bestFrame = frame;
            bestDiff = diff;
          }
        }
        
        return bestFrame;
      };

      const bufferInfo = {
        frameCount: frames.length,
        duration: frames.length > 0 ? frames[frames.length - 1].t - frames[0].t : 0,
        firstFrame: frames.length > 0 ? frames[0].t : 0,
        lastFrame: frames.length > 0 ? frames[frames.length - 1].t : 0,
      };

      return {
        getFrameAt,
        ready: true,
        bufferInfo,
        dispose: () => {
          frames.length = 0;
        }
      };
    } catch (error) {
      console.error('[TelemetryService] Error creating reader from analysis data:', error);
      return null;
    }
  }

  /**
   * Dispose of telemetry data for a video
   */
  disposeTelemetry(videoId: string): void {
    const reader = this.readers.get(videoId);
    if (reader) {
      reader.dispose();
      this.readers.delete(videoId);
    }
    
    this.loadingPromises.delete(videoId);
  }

  /**
   * Dispose of all telemetry data
   */
  disposeAll(): void {
    for (const reader of this.readers.values()) {
      reader.dispose();
    }
    this.readers.clear();
    this.loadingPromises.clear();
  }

  /**
   * Get loading state for a video
   */
  isLoading(videoId: string): boolean {
    return this.loadingPromises.has(videoId);
  }

  /**
   * Check if telemetry data is available for a video
   */
  hasData(videoId: string): boolean {
    return this.readers.has(videoId);
  }
}

// Export singleton instance
export const telemetryService = new TelemetryService();
export default telemetryService;
